import { useInfiniteQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

import { ALL_EVENT_TYPES } from './timelineFilterDefaults';
import type {
  AssociationPayload,
  TimelineEvent,
  TimelineFilters,
  ViewStateLookups,
  ViewStateResponse,
} from './types';
import { EVENT_TYPES } from './types';

// Mirrors backend PayloadProjection (apps/crm_events/core/results.py:43-53).
// Sent as the `payload` query param; backend rejects other values with 400.
export type PayloadProjection = 'full' | 'lean' | 'none';

/** Stable empty filters object to avoid recreating on each call */
const EMPTY_FILTERS: Partial<TimelineFilters> = {};

/**
 * Metadata key stamped on the synthetic events note-to-task conversion
 * produces (the derived task's ObjectCreatedEvent, its translated
 * AssociationAddedEvents, and the note's ObjectMergedEvent) — mirrors
 * apps/crm_events/core/constants.py::NOTE_TO_TASK_CONVERSION_METADATA_KEY.
 */
const NOTE_TO_TASK_CONVERSION_METADATA_KEY = 'ahoy_note_to_task_conversion';

function isConversionMarked(event: TimelineEvent): boolean {
  return Boolean(event.metadata?.[NOTE_TO_TASK_CONVERSION_METADATA_KEY]);
}

/**
 * Prunes conversion-synthetic events — and everything they caused — from a
 * fetched view-state page.
 *
 * Two kinds of event are dropped:
 *
 * 1. Events that carry the marker themselves: the derived task's
 *    ObjectCreatedEvent, its translated AssociationAddedEvents, the note's
 *    ObjectMergedEvent, and any reminder deletes.
 * 2. Events whose causal lineage (`source_event_ids`) contains a marked
 *    event. The backend's cascades — notably MergeAssociationCascade, which
 *    strips the note's edges and re-adds them on the task at conversion time
 *    — emit their own events with EMPTY metadata, so the marker is never
 *    inherited. Without this, a converted task's timeline opened with a burst
 *    of "Ahoy cleared Status / Owner / Creator …" rows at conversion time.
 *    Lineage is the reliable signal: `build_cascade_source_event_ids` appends
 *    the triggering event's id, so every cascade descendant of a marked event
 *    names it in `source_event_ids`.
 *
 * Marked ids are collected from BOTH the page events and the `lookups.events`
 * causal-parent pool, and marked/descendant events are removed from that pool
 * too — otherwise a filtered-out merge event could be re-synthesized from the
 * pool as a de-emphasized context row above its (also filtered) children.
 *
 * This is a UI-only presentation choice, not a data-hiding mechanism: these
 * events remain `timeline_visible=true` in storage, are still returned by
 * this same endpoint (this filter runs after the fetch), and stay visible to
 * the reasoning tool and any direct API caller. The record UI hides them
 * because they carry no independent user-facing meaning — the note's real
 * pre-conversion history (surfaced on the task's timeline by the backend's
 * merge fold-in) is what a user actually wants to see.
 */
function pruneConversionSyntheticEvents(
  response: ViewStateResponse
): ViewStateResponse {
  const pooled = (response.lookups?.events ?? {}) as Record<
    string,
    TimelineEvent
  >;
  const markedIds = new Set<string>();
  for (const event of response.events) {
    if (isConversionMarked(event)) {
      markedIds.add(String(event.event_id));
    }
  }
  for (const event of Object.values(pooled)) {
    if (isConversionMarked(event)) {
      markedIds.add(String(event.event_id));
    }
  }
  if (markedIds.size === 0) {
    return response;
  }
  const isSyntheticOrDescendant = (event: TimelineEvent): boolean =>
    isConversionMarked(event) ||
    (event.source_event_ids ?? []).some(id => markedIds.has(String(id)));

  const events = response.events.filter(
    event => !isSyntheticOrDescendant(event)
  );
  const keptPool = Object.fromEntries(
    Object.entries(pooled).filter(
      ([, event]) => !isSyntheticOrDescendant(event)
    )
  );
  return {
    ...response,
    events,
    lookups: { ...response.lookups, events: keptPool },
  };
}

/**
 * Drops `ahoy_association_removed` rows whose target entity cannot be resolved
 * to a name — the rows the timeline would otherwise render as
 * "removed <association> … Unknown".
 *
 * WHY (issue #13312): note→task conversion deletes the note's reminder. The
 * DeleteCleanupCascade then emits an `ahoy_association_removed` for
 * `ahoy_subject_to_reminder` on the derived task pointing at the just-deleted
 * reminder, with EMPTY metadata and a `source_event_ids` naming the reminder's
 * (soft-deleted, client-invisible) ObjectDeleted event — so
 * pruneConversionSyntheticEvents is structurally blind to it. The one honest
 * signal left on the row is that its target is unresolvable, so it is dropped
 * here. Works on already-migrated orgs because it is a render-time prune.
 *
 * DELIBERATELY BROADER than the conversion (issue #13312 AC): this also hides a
 * genuine "removed <since-deleted entity>" row anywhere else — a removal whose
 * target no longer exists has no nameable subject, so the row reads "Unknown"
 * with nothing actionable. Rows whose target IS resolvable, whose payload
 * carries an explicit `target_label`, that carry no target id (renderer shows
 * "record"), or whose association targets a meeting (renderer shows "Private
 * meeting", not "Unknown") are all KEPT.
 *
 * Runs per fetched page against that page's `lookups.entities` /
 * `lookups.association_types`. The per-page oracle is sound: the view-state
 * endpoint hydrates each removal's target into its OWN page's `entities` pool
 * (apps/crm_events/services/lookup_hydration.py:287-291), so a resolvable
 * target always co-locates with its removal event. Mirrors
 * pruneConversionSyntheticEvents: filters BOTH the rendered `events` and the
 * `lookups.events` causal-parent pool.
 */
function rendersUnknownRemovalTarget(
  event: TimelineEvent,
  entities: ViewStateLookups['entities'],
  associationTypes: ViewStateLookups['association_types']
): boolean {
  if (event.event_type_name !== EVENT_TYPES.ASSOCIATION_REMOVED) {
    return false;
  }
  const payload = event.payload as AssociationPayload | undefined;
  const targetId = payload?.target_entity_id;
  // No target id → renderer shows "record", not "Unknown" → keep.
  if (!targetId) {
    return false;
  }
  // Explicit label baked into the payload → renderer shows it → keep.
  if (payload?.target_label) {
    return false;
  }
  // Resolvable in the page's entity pool → renderer shows the name → keep.
  if (entities[targetId]) {
    return false;
  }
  // Access-controlled meeting target renders "Private meeting", not "Unknown"
  // (events/AssociationEvent.tsx:109-114) → keep.
  const assocTypeName = payload?.association_type_name;
  const assocType = assocTypeName ? associationTypes[assocTypeName] : undefined;
  if (assocType?.target_entity_type_name === 'ahoy_meeting') {
    return false;
  }
  return true;
}

function pruneUnresolvableAssociationRemovals(
  response: ViewStateResponse
): ViewStateResponse {
  const entities = response.lookups?.entities ?? {};
  const associationTypes = response.lookups?.association_types ?? {};
  const pooled = (response.lookups?.events ?? {}) as Record<
    string,
    TimelineEvent
  >;

  const events = response.events.filter(
    event => !rendersUnknownRemovalTarget(event, entities, associationTypes)
  );
  const keptPool = Object.fromEntries(
    Object.entries(pooled).filter(
      ([, event]) =>
        !rendersUnknownRemovalTarget(event, entities, associationTypes)
    )
  );

  // Nothing dropped → return the original response unchanged.
  if (
    events.length === response.events.length &&
    Object.keys(keptPool).length === Object.keys(pooled).length
  ) {
    return response;
  }

  return {
    ...response,
    events,
    lookups: { ...response.lookups, events: keptPool },
  };
}

/**
 * Options for the useTimelineViewState hook.
 */
interface UseTimelineViewStateOptions {
  /** Entity ID to fetch events for */
  entityId: string;
  /** Organization ID for the API endpoint */
  orgId: string;
  /** Entity type of the current view (e.g., 'ahoy_contact') */
  entityType?: string;
  /** Optional filters to apply to events */
  filters?: Partial<TimelineFilters>;
  /** Whether to enable the query. Default: true */
  enabled?: boolean;
  /** Payload projection tier. Omitted from the request when undefined (backend defaults to full). */
  payload?: PayloadProjection;
  /** Request page size. Defaults to 50 when undefined. */
  limit?: number;
}

/**
 * Fetch a single page from the view-state API.
 */
async function fetchViewStatePage(
  orgId: string,
  entityId: string,
  filters: Partial<TimelineFilters>,
  cursor?: string,
  payload?: PayloadProjection,
  limit?: number
): Promise<ViewStateResponse> {
  const params = new URLSearchParams({
    limit: String(limit ?? 50),
    include_associated: String(filters.includeAssociated ?? true),
  });

  if (cursor) {
    params.set('cursor', cursor);
  }

  // Send concrete event-type names only when a strict subset is selected.
  // Empty eventTypes or all-types-selected → omit params → server returns all.
  const { eventTypes } = filters;
  if (
    eventTypes &&
    eventTypes.length > 0 &&
    eventTypes.length < ALL_EVENT_TYPES.length
  ) {
    for (const eventType of eventTypes) {
      params.append('event_type_name', eventType);
    }
  }

  if (payload) {
    params.set('payload', payload);
  }

  const url = `/api/orgs/${orgId}/crm-events/entities/${entityId}/view-state/?${params}`;

  return apiGet<ViewStateResponse>(url);
}

/**
 * Merge lookups from multiple pages into a single lookup object.
 *
 * Later pages may have different entities/users/properties, so we need
 * to combine all lookups to support all events across all pages.
 *
 * @param pages - Array of ViewStateResponse pages
 * @returns Combined lookups from all pages
 */
export function mergeLookups(pages: ViewStateResponse[]): ViewStateLookups {
  const merged: ViewStateLookups = {
    entities: {},
    users: {},
    emails: {},
    audits: {},
    forms: {},
    property_types: {},
    association_types: {},
    events: {},
  };

  for (const page of pages) {
    Object.assign(merged.entities, page.lookups.entities ?? {});
    Object.assign(merged.users, page.lookups.users ?? {});
    Object.assign(merged.emails, page.lookups.emails ?? {});
    Object.assign(merged.forms, page.lookups.forms ?? {});
    Object.assign(merged.property_types, page.lookups.property_types ?? {});
    Object.assign(
      merged.association_types,
      page.lookups.association_types ?? {}
    );
    Object.assign(
      merged.events as Record<string, TimelineEvent>,
      page.lookups.events ?? {}
    );

    // Audits are arrays per event_id — concat rather than overwrite
    for (const [eventId, auditIds] of Object.entries(
      page.lookups.audits ?? {}
    )) {
      if (!merged.audits[eventId]) {
        merged.audits[eventId] = [];
      }
      merged.audits[eventId].push(...auditIds);
    }
  }

  return merged;
}

/**
 * Filter events client-side based on non-type criteria.
 *
 * Event-type filtering is handled server-side via the `event_type_name` query
 * params built in fetchViewStatePage. Only date-range and actor filters remain
 * client-side because they are not sent to the server.
 *
 * @param events - Array of timeline events
 * @param filters - Filter criteria
 * @returns Filtered events
 */
function filterEvents(
  events: TimelineEvent[],
  filters: Partial<TimelineFilters>
): TimelineEvent[] {
  let filtered = [...events];

  // Filter by date range
  if (filters.startDate) {
    const { startDate } = filters;
    filtered = filtered.filter(event => {
      if (!event.occurred_at) {
        return true;
      }
      const eventDate = new Date(event.occurred_at);
      return eventDate >= startDate;
    });
  }

  if (filters.endDate) {
    const { endDate } = filters;
    filtered = filtered.filter(event => {
      if (!event.occurred_at) {
        return true;
      }
      const eventDate = new Date(event.occurred_at);
      return eventDate <= endDate;
    });
  }

  // Filter by actor (source_id)
  if (filters.actorId) {
    filtered = filtered.filter(event => event.source_id === filters.actorId);
  }

  return filtered;
}

/**
 * Hook for fetching timeline events from the /view-state/ API with infinite scroll.
 *
 * Uses TanStack Query's useInfiniteQuery for cursor-based pagination.
 * Automatically handles:
 * - Fetching pages of events
 * - Merging lookups across pages
 * - Client-side filtering
 * - Bundling consecutive property updates
 *
 * @example
 * ```tsx
 * function Timeline({ objectId, orgId }) {
 *   const {
 *     data,
 *     fetchNextPage,
 *     hasNextPage,
 *     isFetchingNextPage,
 *     isLoading,
 *     error,
 *   } = useTimelineViewState({
 *     entityId: objectId,
 *     orgId,
 *     filters: { includeAssociated: true },
 *   });
 *
 *   // Flatten events from all pages
 *   const events = data?.pages.flatMap(page => page.events) ?? [];
 *
 *   // Merge lookups from all pages
 *   const lookups = data?.pages ? mergeLookups(data.pages) : undefined;
 *
 *   return (
 *     <TimelineList
 *       events={events}
 *       lookups={lookups}
 *       onLoadMore={() => fetchNextPage()}
 *       hasMore={hasNextPage}
 *     />
 *   );
 * }
 * ```
 */
export function useTimelineViewState({
  entityId,
  orgId,
  entityType,
  filters = EMPTY_FILTERS,
  enabled = true,
  payload,
  limit,
}: UseTimelineViewStateOptions) {
  return useInfiniteQuery({
    queryKey: [
      'entity-view-state',
      entityId,
      orgId,
      entityType,
      filters,
      payload,
      limit,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await fetchViewStatePage(
        orgId,
        entityId,
        filters,
        pageParam,
        payload,
        limit
      );

      // Apply client-side filtering — first the always-on conversion-synthetic
      // prune (page events AND the causal-parent pool), then the user-facing
      // filter criteria (date range, actor).
      const pruned = pruneConversionSyntheticEvents(response);
      const withoutUnknownRemovals =
        pruneUnresolvableAssociationRemovals(pruned);
      const events = filterEvents(withoutUnknownRemovals.events, filters);

      return {
        ...withoutUnknownRemovals,
        events,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      if (!lastPage.pagination.has_more) {
        return undefined;
      }
      return lastPage.pagination.next_cursor;
    },
    enabled: enabled && !!entityId && !!orgId,
    // Rely on the global staleTime (5 min) to control freshness. The
    // previous `refetchOnMount: 'always'` fired a new request on every
    // component mount even when the data was seconds old — slideout
    // open/close cycles and parent re-renders that unmount/remount
    // TimelineSection caused 10+ redundant page-1 fetches per entity.
    //
    // Timeline data stays fresh via two mechanisms:
    //   1. WebSocket events → invalidateTimelineQueries() marks this
    //      query stale → React Query refetches active observers.
    //   2. The global staleTime (5 min) ensures stale data is refreshed
    //      on the next mount.
    //
    // `refetchOnWindowFocus` inherits the global `false` — the WS +
    // poller handle live updates without per-focus refetches.
  });
}
